import { StorageIntegration } from "@/schema"
import { useQueries, useQuery } from "@tanstack/react-query"

import { useApiContext } from "@/hooks/api/use-api-context"
import { useBookId } from "@/hooks/use-book-id"

import {
  browseStorage,
  browseStorageForPicker,
  fetchStorageIntegration,
  fetchStorageIntegrations,
  getStorageIntegrationAccessToken,
} from "./api"

export const storageIntegrationsQueryKeys = {
  all: ["storage_integrations"] as const,
  lists: () => [...storageIntegrationsQueryKeys.all, "list"] as const,
  list: (bookId?: string) => [...storageIntegrationsQueryKeys.lists(), bookId] as const,
  details: () => [...storageIntegrationsQueryKeys.all, "detail"] as const,
  detail: (id: string, bookId?: string) => [...storageIntegrationsQueryKeys.details(), id, bookId] as const,
  browse: (integrationId: string, folderId?: string, bookId?: string) =>
    [...storageIntegrationsQueryKeys.all, "browse", integrationId, folderId, bookId] as const,
  pickerItems: (integrationId: string, folderId?: string, bookId?: string) =>
    [...storageIntegrationsQueryKeys.all, "picker_items", integrationId, folderId, bookId] as const,
  pickerSearch: (integrationId: string, query: string, bookId?: string) =>
    [...storageIntegrationsQueryKeys.all, "picker_search", integrationId, query, bookId] as const,
  accessToken: (id: string, bookId?: string) =>
    [...storageIntegrationsQueryKeys.detail(id, bookId), "access_token"] as const,
}

export function useStorageIntegrations(bookId?: string) {
  const urlBookId = useBookId()
  const resolvedBookId = bookId ?? urlBookId
  const getContext = useApiContext(bookId)

  return useQuery({
    queryKey: storageIntegrationsQueryKeys.list(resolvedBookId),
    queryFn: async () => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      const response = await fetchStorageIntegrations(context)
      return response.storage_integrations
    },
  })
}

export function useStorageIntegration(id: string, bookId?: string) {
  const urlBookId = useBookId()
  const resolvedBookId = bookId ?? urlBookId
  const getContext = useApiContext(bookId)

  return useQuery<StorageIntegration>({
    queryKey: storageIntegrationsQueryKeys.detail(id, resolvedBookId),
    queryFn: async () => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return fetchStorageIntegration(context, id)
    },
    enabled: !!id,
  })
}

export function useBrowseStorage(
  integrationId: string,
  folderId?: string,
  options?: { enabled?: boolean; bookId?: string }
) {
  const urlBookId = useBookId()
  const resolvedBookId = options?.bookId ?? urlBookId
  const getContext = useApiContext(options?.bookId)

  return useQuery({
    queryKey: storageIntegrationsQueryKeys.browse(integrationId, folderId, resolvedBookId),
    queryFn: async () => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return browseStorage(context, { integrationId, folderId })
    },
    enabled: !!integrationId && options?.enabled !== false,
  })
}

export function useStoragePickerItems(
  integrationId: string,
  folderId?: string,
  options?: { enabled?: boolean; bookId?: string }
) {
  const urlBookId = useBookId()
  const resolvedBookId = options?.bookId ?? urlBookId
  const getContext = useApiContext(options?.bookId)

  return useQuery({
    queryKey: storageIntegrationsQueryKeys.pickerItems(integrationId, folderId, resolvedBookId),
    queryFn: async () => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return browseStorageForPicker(context, { integrationId, folderId })
    },
    enabled: !!integrationId && options?.enabled !== false,
  })
}

// Provider-side name search for the link picker. Searches the whole connected drive,
// not just the folder currently open, so files outside the record folder stay reachable.
export function useStoragePickerSearch(
  integrationId: string,
  query: string,
  options?: { enabled?: boolean; bookId?: string }
) {
  const urlBookId = useBookId()
  const resolvedBookId = options?.bookId ?? urlBookId
  const getContext = useApiContext(options?.bookId)

  const results = useQueries({
    queries: [
      {
        queryKey: storageIntegrationsQueryKeys.pickerSearch(integrationId, query, resolvedBookId),
        queryFn: async ({ signal }: { signal: AbortSignal }) => {
          const context = await getContext()
          if (!context) throw new Error("No context found")
          return browseStorageForPicker(context, { integrationId, query, signal })
        },
      },
    ].filter(() => !!integrationId && !!query && options?.enabled !== false),
  })
  return (
    results[0] ?? { data: undefined, error: null, isLoading: false, errorUpdatedAt: 0, refetch: async () => undefined }
  )
}

export function useStorageIntegrationAccessToken(integrationId: string | undefined, bookId?: string) {
  const urlBookId = useBookId()
  const resolvedBookId = bookId ?? urlBookId
  const getContext = useApiContext(bookId)

  return useQuery({
    queryKey: storageIntegrationsQueryKeys.accessToken(integrationId || "", resolvedBookId),
    queryFn: async () => {
      const context = await getContext()
      if (!context || !integrationId) throw new Error("No context or integration ID found")
      const response = await getStorageIntegrationAccessToken(context, integrationId)
      return response.access_token
    },
    enabled: !!integrationId,
  })
}
